import { Box, Slide, Backdrop, Portal, styled, Breakpoint, Theme } from '@mui/material';
import { ReactNode, useCallback, useMemo, MouseEvent } from 'react';

import { SizingMultiplier } from '@ocx/ui';

/** Max width of the drawer content. Accepts MUI breakpoint key or number (px). Defaults to 'sm' (600px). */
type MaxWidthProp = Exclude<Breakpoint, 'xs'> | number; // xs omitted because theme.breakpoints.values.xs is 0

const ANIMATION_TIMEOUT = 300;
const DECORATOR_HEIGHT = '65px';
const DEFAULT_MAX_WIDTH: MaxWidthProp = 'sm';

export const ModalPaper = styled('div')(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.spacing(SizingMultiplier.md, SizingMultiplier.md, 0, 0),
  [theme.breakpoints.up('sm')]: {
    borderRadius: theme.spacing(SizingMultiplier.md),
  },
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'center',
}));

type ModalDrawerProps = {
  open: boolean;
  children: ReactNode;
  minPaperHeight?: number;
  isDecorated?: boolean;
  backgroundImageSrc?: string;
  maxWidth?: MaxWidthProp;
  onClose?(): void;
  onEnter?(): void;
  onExited?(): void;
};

export const ModalDrawer = (props: ModalDrawerProps) => {
  const {
    open = false,
    children,
    onClose,
    onEnter,
    onExited,
    minPaperHeight = 100,
    isDecorated,
    backgroundImageSrc,
    maxWidth = DEFAULT_MAX_WIDTH,
  } = props;

  const onClick = useCallback(
    (e: MouseEvent) => {
      if (onClose !== undefined && e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const resolvedMaxWidth = useMemo(() => {
    if (typeof maxWidth === 'number') return maxWidth;
    return (theme: Theme) => theme.breakpoints.values[maxWidth];
  }, [maxWidth]);

  return (
    <Portal>
      <Backdrop
        open={open}
        aria-hidden={!open}
        onClick={onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-end', sm: 'center' },
          alignItems: 'center',
          backgroundImage: backgroundImageSrc ? `url(${backgroundImageSrc})` : undefined,
          backgroundSize: 'cover',
        }}
        timeout={ANIMATION_TIMEOUT}>
        <Slide
          direction="up"
          appear
          in={open}
          onEnter={onEnter}
          onExited={onExited}
          timeout={ANIMATION_TIMEOUT}
          unmountOnExit>
          {/* Box used to prevent "decorators" display partially during animation */}
          {/* Box should not have role=button and tabIndex to prevent element from focusing */}
          <Box
            sx={{
              overflow: 'auto',
              overscrollBehavior: 'contain',
              pt: `calc(${DECORATOR_HEIGHT} + var(--ion-safe-area-top, 0px))`,
              px: { xs: 0, sm: SizingMultiplier.lg },
              pb: { xs: 0, sm: SizingMultiplier.lg },
              outline: 'none',
              width: '100%',
              maxWidth: resolvedMaxWidth,
            }}
            onClick={onClick}>
            <ModalPaper sx={{ minHeight: minPaperHeight, paddingTop: isDecorated ? DECORATOR_HEIGHT : undefined }}>
              {children}
            </ModalPaper>
          </Box>
        </Slide>
      </Backdrop>
    </Portal>
  );
};
